<template>
  <div class="chart-databutton column gap-1">
    <div class="line gap-2">
      <slot name="item">
        <button-item
          :icons="removable ? ['x'] : []"
          @click:x="$emit('remove')"
          @click="onMainButtonClick($event)"
        >
          <slot>--title--</slot>
        </button-item>
      </slot>

      <div v-if="colorsAllowed" class="line gap-0">
        <button
          v-if="colorsAllowed && openable"
          type="button"
          class="button-icon p-2"
          style="width: 20px"
          @click="setItemOpen(!isOpen)"
        >
          <lucide-icon :name="isOpen ? 'chevron-up' : 'chevron-down'" />
        </button>
        <design-button
          v-if="!isOpen"
          :color="safeColor"
          type="color"
          button-style="flat"
          :null-allowed="true"
          @update:color="setItemColor(itemType, itemId, $event)"
        ></design-button>
        <div v-else style="width: 32px"></div>
      </div>
    </div>
    <div v-if="colorsAllowed && isOpen && openable" class="column gap-1">
      <div
        v-for="simulation in ctx.selectedSimulations"
        :key="simulation.id"
        class="line_toggle pl-4"
      >
        <span class="evaluation-databutton__text">{{ simulation.title }}</span>
        <design-button
          :symbol="simconfigs?.[simulation.id]?.symbol || 'circle'"
          :color="config?.[simulation.id]?.color ?? safeColor"
          :filled="isFilled"
          type="color"
          :null-allowed="true"
          @update:color="setItemColor(itemType, itemId, $event, simulation.id)"
          @update:symbol="
            setItemSymbol(itemType, itemId, $event, simulation.id)
          "
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { showMessage } from "@core/ui/alert/alert";
import { useEvaluationStore } from "../evaluation/evaluations.store";
import { useReactiveChartData } from "./useChartData";
import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useChartStore } from "./charts.store";

import DesignButton from "@core/input/DesignButton.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import type { ConfigurableType } from "@editor/items/charts/ChartItem";
import type { UnfilledSymbolName } from "@core/ui/svg-icons/symbols/SymbolName";
import { toUid, type Uid } from "@/helpers/utility/Id";
import { useSceneTree } from "../scene/sceneTree";

interface Props {
  chartUid: Uid<"evaluation" | "chart">;
  itemType: ConfigurableType;
  itemId: number | string;
  subgroup?: string;
  removable?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  removable: true,
  subgroup: undefined
});

defineEmits<{
  (e: "remove"): void;
}>();

const { t } = useI18n();

const store = computed(() => {
  if (props.chartUid.startsWith("evaluation")) {
    return useEvaluationStore();
  }
  else if (props.chartUid.startsWith("chart")) {
    return useChartStore();
  }
  throw new Error("Invalid chart UID");
});

const item = computed(() => {
  return store.value.byUid[props.chartUid];
});

const ctx = useReactiveChartData(() => item.value);

const simconfigs = computed(() => {
  return ctx.itemConfigs.simulation;
});

const config = computed(() => {
  if (props.subgroup) {
    return ctx.itemConfigs?.[props.itemType]?.[props.itemId]?.subgroups?.[
      props.subgroup
    ];
  }
  return ctx.itemConfigs?.[props.itemType]?.[props.itemId];
});

// ===============
// COLORS

const colorsAllowed = computed(() => {
  if (props.itemType == "simulationRound")
    return ctx.chartAxisConfig?.hasRoundColors;
  return ctx.chartAxisConfig?.hasItemColors;
});

const hasDiverseColors = computed(() => {
  const mainColor = safeColor.value;
  let lastColor = null;
  for (const simulation of ctx.selectedSimulations) {
    const simcolor = config.value?.[simulation.id]?.color ?? mainColor;
    if (simcolor == null) continue;
    if (lastColor == null) lastColor = simcolor;
    if (lastColor != simcolor) return true;
  }
  return false;
});

const configElementItem = computed(() => {
  if (props.itemType == "target") {
    return useTargetStore().items[props.itemId as number];
  }
  if (props.itemType == "resource") {
    return useResourceStore().items[props.itemId as number];
  }
  if (props.itemType == "index") {
    return useIndexStore().items[props.itemId as number];
  }
});

const sceneTree = useSceneTree();

const defaultColor = computed(() => {
  const standardColor = "var(--color-text)";
  if (!configElementItem.value) return standardColor;

  const treeNode = sceneTree.elementNodes.get(
    toUid(props.itemType, props.itemId) as Uid<"target" | "resource" | "index">
  );

  return treeNode?.node?.fill || standardColor;
});

const safeColor = computed(() => {
  return config.value?.color ?? defaultColor.value;
});

const isFilled = computed(() => {
  return props.itemId == "total";
});

function setItemColor(
  itemType: ConfigurableType,
  itemId: number | string,
  color: string | null,
  simulationId?: number
) {
  store.value.setItemColor(
    item.value.id,
    itemType,
    itemId,
    color,
    simulationId,
    props.subgroup
  );
}

function onMainButtonClick(event: MouseEvent) {
  event.preventDefault();
}

// ===============
// OPEN / CLOSE

/**
 * Is there only ONE simulation selected?
 * Items can only be opened if there are multiple simulations!
 */
const singleSimulation = computed(() => {
  return (
    !!ctx.chartAxisConfig?.singleSimulation ||
    ctx.selectedSimulations.length <= 1
  );
});

const isOpen = computed(() => {
  if (singleSimulation.value) return hasDiverseColors.value;
  return hasDiverseColors.value || !!config.value?.open;
});

const openable = computed(() => {
  return hasDiverseColors.value || singleSimulation.value == false;
});

function setItemOpen(open: boolean) {
  if (open) {
    return store.value.updateItemConfig(
      item.value.id,
      props.itemType,
      props.itemId,
      props.subgroup,
      { open: true }
    );
  }

  let color: string | null | undefined = safeColor.value;

  // prevent close if the item has different colors per simulation
  if (!open && hasDiverseColors.value) {
    showMessage(t("messages.cantCloseItemWithMultipleColors"));
    return false;
  }
  // update the main color if all simulations have the same.
  else if (!open) {
    // get the first simulation that has a set color
    const sim = ctx.selectedSimulations.find(
      (sim) => config.value?.[sim.id]?.color != null
    );
    if (sim) {
      const simColor = config.value?.[sim.id]?.color;
      if (simColor != color) {
        color = simColor;
      }
    }
  }

  store.value.closeItemConfig(
    item.value.id,
    props.itemType,
    props.itemId,
    props.subgroup,
    { color }
  );
}

// ===============
// SYMBOLS

function setItemSymbol(
  itemType: ConfigurableType,
  itemId: number | string,
  symbol: UnfilledSymbolName,
  simulationId?: number
) {
  store.value.setItemSymbol(
    item.value.id,
    itemType,
    itemId ?? simulationId,
    symbol,
    props.subgroup
  );
}
</script>

<style lang="scss">
.chart-databutton {
  &__text {
    font-size: 12px;
    padding-block: 0.25rem;
  }
}
</style>
